/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Prompt view insight panels — the dependency graph, the run ledger and the
 * observability of one prompt, split into three self-contained cards so the
 * Prompt view can drop them in without growing more state of its own.
 *
 *   - PromptDependenciesPanel  — what the prompt needs (upstream) and what
 *     embeds or targets it (downstream, from /prompts/{id}/dependents).
 *   - PromptRunHistoryPanel    — the prompt's own execution ledger (cost,
 *     tokens, latency, status), separate from the Eval Run History.
 *   - PromptObservabilityPanel — a health summary over the prompt's traces,
 *     each openable in full inline, with a deep-link to AI Observability.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Tag } from 'primereact/tag';
import { Dialog } from 'primereact/dialog';
import { formatDate } from 'sharedUi/dateUtils';

import { promptsService, type PromptDependentGroup } from '../../services/prompts.service';
import { promptExecutionsService } from '../../services/prompt-executions.service';
import { aiObservabilityService } from '../../services/ai-observability.service';
import { fmtCost, fmtMs, type ObsTraceRow } from '../../types/ai-observability.types';
import ObsTracingDetail from '../../pages/ai-observability/obs-tracing-detail';

/** Base64 URL-safe id encoding — matches useSecureNavigation so a built link
 *  lands on the same encoded view route the app navigates to internally. */
const encodeId = (id: string): string => {
  try {
    return btoa(id).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  } catch {
    return id;
  }
};

/** Dependent types whose row id maps cleanly to a view route. RAG rows are a
 *  version id (not the RAG master) and conversations have no stable view, so
 *  those are shown as plain chips rather than broken links. */
const LINK_BASE: Record<string, string> = { agent: '/agents', eval_suite: '/evals' };

/** Every master/collection that can reference a prompt — always listed (0 when
 *  none), so a viewer or a demo sees all the places a prompt can be used, not
 *  only the ones that happen to be wired today. Most-to-least common. */
const DEP_CATEGORIES: { type: PromptDependentGroup['type']; label: string }[] = [
  { type: 'agent', label: 'Agents' },
  { type: 'rag', label: 'RAG versions' },
  { type: 'eval_suite', label: 'Eval suites' },
  { type: 'conversation', label: 'Conversations' },
  { type: 'document_intelligence', label: 'Document Intelligence' },
];

/** A subtle, truncating reference pill — a link when base + id are known. */
function RefChip({ base, id, label, severity }: Readonly<{
  base?: string; id?: string | null; label: string; severity: 'info' | 'secondary';
}>) {
  const toneCls = severity === 'info'
    ? 'border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-900/50 dark:bg-indigo-900/20 dark:text-indigo-300'
    : 'border-gray-200 bg-gray-50 text-ink dark:border-gray-700 dark:bg-gray-800/60';
  const cls = `inline-flex max-w-[260px] items-center truncate rounded-md border px-2 py-0.5 text-xs transition-colors ${toneCls}`;
  if (base && id) {
    return <Link to={`${base}/${encodeId(id)}`} title={label}
      className={`${cls} hover:border-blue-400 hover:text-blue-600`}>{label}</Link>;
  }
  return <span title={label} className={cls}>{label}</span>;
}

export function PromptDependenciesPanel({ promptId }: Readonly<{ promptId: string }>) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['prompt-dependents', promptId],
    queryFn: () => promptsService.getDependents(promptId),
    enabled: !!promptId,
    staleTime: 30 * 1000,
  });

  const byType = new Map((data?.groups ?? []).map((g) => [g.type, g]));

  return (
    <div className="ui-panel p-5" data-testid="PromptView.Dependencies">
      <h2 className="text-sm font-semibold text-ink mb-0.5">Used by</h2>
      <p className="text-xs text-ink-muted mb-3">
        Everything across the platform that references this prompt — the model, project and evals
        it depends on are in the Details panel. Click any name to open its record.
      </p>

      {/* Every master/collection that can reference this prompt, always listed
          with its count; non-empty ones link to each record. */}
      <div>
        <span className="text-xs text-ink-muted block mb-1">
          {data ? `${data.total} reference${data.total === 1 ? '' : 's'} in total` : 'References'}
        </span>
        {isLoading && <p className="text-sm text-ink-muted">Loading dependents…</p>}
        {isError && <p className="text-sm text-ink-muted">Could not load dependents.</p>}
        {!isLoading && !isError && (
          <div className="flex flex-col gap-2">
            {DEP_CATEGORIES.map((cat) => {
              const g = byType.get(cat.type);
              const count = g?.count ?? 0;
              return (
                <div key={cat.type}>
                  <div className="text-xs font-medium text-ink mb-1">{cat.label} · {count}</div>
                  {count > 0 && g ? (
                    <div className="flex flex-wrap gap-1.5">
                      {g.items.map((it, i) => (
                        <RefChip key={it.id ?? i} severity="secondary"
                          base={LINK_BASE[g.type]} id={it.id} label={it.name} />
                      ))}
                      {g.count > g.items.length && (
                        <span className="text-xs text-ink-muted self-center">
                          +{g.count - g.items.length} more
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-ink-muted">None</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const runStatusSeverity = (s?: string | null): 'success' | 'danger' | 'info' => {
  const v = (s || '').toUpperCase();
  if (v === 'SUCCESS') return 'success';
  if (v === 'FAILED') return 'danger';
  return 'info';
};

export function PromptRunHistoryPanel({ promptId }: Readonly<{ promptId: string }>) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['prompt-run-history', promptId],
    queryFn: () => promptExecutionsService.listByPrompt(promptId, { page: 1, page_size: 10 }),
    enabled: !!promptId,
    staleTime: 30 * 1000,
  });

  const runs = data?.items ?? [];

  return (
    <div className="ds-card lg:col-span-2 p-3" data-testid="PromptView.RunHistory">
      <div className="flex items-start justify-between mb-2 gap-2">
        <div>
          <h2 className="text-sm font-semibold text-ink">Run History</h2>
          <p className="text-xs text-ink-muted">
            Every time this prompt was executed — model, tokens, cost, latency and outcome.
            Separate from the Eval Run History above.
          </p>
        </div>
        {data && data.total > 0 && (
          <span className="text-xs text-ink-muted whitespace-nowrap">{data.total} total</span>
        )}
      </div>

      {isLoading && <p className="text-sm text-ink-muted">Loading runs…</p>}
      {isError && <p className="text-sm text-ink-muted">Could not load run history.</p>}
      {!isLoading && !isError && runs.length === 0 && (
        <p className="text-sm text-ink-muted">
          No runs yet. Use <span className="font-medium">Test Run</span> to execute this prompt.
        </p>
      )}
      {!isLoading && runs.length > 0 && (
        <div className="flex flex-col divide-y divide-gray-100 dark:divide-gray-700">
          {runs.map((r: any) => {
            const tokens = (r.inputTokenCount ?? 0) + (r.outputTokenCount ?? 0);
            return (
              <div key={r._id} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-ink truncate">
                    {r.llmModel || r.prompt?.llm || 'Model —'}
                  </div>
                  <div className="text-xs text-ink-muted whitespace-nowrap">
                    {formatDate(r.createdAt)}
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0 text-xs text-ink-muted">
                  <span className="whitespace-nowrap">{tokens.toLocaleString()} tok</span>
                  <span className="whitespace-nowrap">{fmtCost(r.cost ?? null, r.currency)}</span>
                  <span className="whitespace-nowrap">
                    {r.executionTime != null ? fmtMs(r.executionTime * 1000) : '—'}
                  </span>
                  <Tag value={r.status || 'unknown'} severity={runStatusSeverity(r.status)}
                    className="text-xs px-1.5 py-0.5" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const OBS_RANGES = [
  { label: 'Last 24 hours', value: '24h' },
  { label: 'Last 7 days', value: '7d' },
  { label: 'Last 30 days', value: '30d' },
];

/** p-th percentile of a list, or null when empty. */
const percentile = (xs: number[], p: number): number | null => {
  if (!xs.length) return null;
  const sorted = [...xs].sort((a, b) => a - b);
  const idx = Math.min(sorted.length - 1, Math.floor((p / 100) * sorted.length));
  return sorted[idx];
};

export function PromptObservabilityPanel({ promptId }: Readonly<{ promptId: string }>) {
  const [range, setRange] = useState('7d');
  const [openTrace, setOpenTrace] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['prompt-observability', promptId, range],
    queryFn: () => aiObservabilityService.getTracing({ promptId, timeRange: range, limit: 50 }),
    enabled: !!promptId,
    staleTime: 30 * 1000,
  });

  const rows: ObsTraceRow[] = data?.traces ?? [];
  const summary = data?.summary;

  const kpis = useMemo(() => {
    const calls = summary?.calls ?? 0;
    const failed = summary?.failed ?? 0;
    const latencies = rows.map((r) => r.latencyMs).filter((v): v is number => v != null);
    const costs = rows.map((r) => r.cost).filter((v): v is number => v != null);
    const currencies = new Set(rows.map((r) => r.currency).filter(Boolean));
    const totalCost = costs.length ? costs.reduce((a, b) => a + b, 0) : null;
    const avg = latencies.length ? latencies.reduce((a, b) => a + b, 0) / latencies.length : null;
    return {
      calls,
      successRate: calls > 0 ? `${Math.round(((calls - failed) / calls) * 100)}%` : '—',
      avgLatency: fmtMs(avg),
      p95Latency: fmtMs(percentile(latencies, 95)),
      totalCost: fmtCost(totalCost, currencies.size === 1 ? [...currencies][0] : null),
    };
  }, [summary, rows]);

  const deepLink = `/ai-observability/tracing?promptId=${promptId}`;

  return (
    <div className="ds-card lg:col-span-2 p-3" data-testid="PromptView.Observability">
      <div className="flex items-start justify-between mb-2 gap-2">
        <div>
          <h2 className="text-sm font-semibold text-ink">Observability</h2>
          <p className="text-xs text-ink-muted">
            Health of this prompt's traced calls. Open any trace for its full step-by-step waterfall.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <select
            className="text-xs border border-gray-200 dark:border-gray-700 rounded px-1.5 py-1 bg-transparent text-ink"
            value={range} onChange={(e) => setRange(e.target.value)} aria-label="Time range"
          >
            {OBS_RANGES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <Link to={deepLink} className="text-xs text-blue-600 hover:underline whitespace-nowrap">
            Open in AI Observability
          </Link>
        </div>
      </div>

      {isError && <p className="text-sm text-ink-muted">Could not load observability.</p>}

      {/* Summary strip. */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
        {[
          { label: 'Calls', val: isLoading ? '—' : kpis.calls.toLocaleString() },
          { label: 'Success rate', val: isLoading ? '—' : kpis.successRate },
          { label: 'Avg latency', val: isLoading ? '—' : kpis.avgLatency },
          { label: 'p95 latency', val: isLoading ? '—' : kpis.p95Latency },
          { label: 'Total cost', val: isLoading ? '—' : kpis.totalCost },
        ].map((k) => (
          <div key={k.label} className="rounded bg-gray-50 dark:bg-gray-800 p-2">
            <div className="text-[11px] text-ink-muted">{k.label}</div>
            <div className="text-sm font-semibold text-ink tabular-nums">{k.val}</div>
          </div>
        ))}
      </div>

      {/* Recent traces — full detail one click away. */}
      {!isLoading && !isError && rows.length === 0 && (
        <p className="text-sm text-ink-muted">No traced calls in this window.</p>
      )}
      {rows.length > 0 && (
        <div className="flex flex-col divide-y divide-gray-100 dark:divide-gray-700">
          {rows.slice(0, 8).map((r) => {
            const failed = r.status === 'FAILED';
            return (
              <div key={r.observabilityId} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <div className="text-sm text-ink truncate">{r.name || r.modelId || 'AI call'}</div>
                  <div className="text-xs text-ink-muted whitespace-nowrap">{formatDate(r.startedAt)}</div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0 text-xs text-ink-muted">
                  <span className="whitespace-nowrap">{fmtMs(r.latencyMs)}</span>
                  <span className="whitespace-nowrap">{fmtCost(r.cost, r.currency)}</span>
                  <Tag value={failed ? 'failed' : (r.status || 'ok')}
                    severity={failed ? 'danger' : 'success'} className="text-xs px-1.5 py-0.5" />
                  <button type="button" className="text-xs text-blue-600 hover:underline"
                    onClick={() => setOpenTrace(r.observabilityId)}>
                    View trace
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Full trace, inline. */}
      <Dialog
        header="Trace detail"
        visible={!!openTrace}
        onHide={() => setOpenTrace(null)}
        style={{ width: '64rem', maxWidth: '95vw' }}
        maximizable
        dismissableMask
      >
        <div className="max-h-[75vh] overflow-auto">
          {openTrace && <ObsTracingDetail observabilityId={openTrace} embedded />}
        </div>
      </Dialog>
    </div>
  );
}
